import classNames from 'classnames';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import isFunction from 'lodash/isFunction';

import { AnyExportStatusReturnType } from 'src/api/taskStatus';
import { UiButton } from 'src/components/UI/Button';
import { UiTableInnerFiltersType } from 'src/components/UI/table/UiTable.helpers';
import { ConfirmContext } from 'src/containers/Common';
import { APIExportStartReturnType } from 'src/models/BaseApi';
import { dispatchAnyRequestStart } from 'src/store/anyRequest/helpers';
import { AnyRequestActionType } from 'src/store/anyRequest/slice';
import { currentUserSelector } from 'src/store/user/selectors';
import { useAnyRequest } from 'src/utils/hooks/useAnyRequest';
import { useRequest } from 'src/utils/hooks/useRequest';
import { PubNub } from 'src/utils/PubNub';
import { SafeAnyType } from 'src/utils/safeAny';
import { useTHelper } from 'src/utils/i18n';

type Props = {
  checkStatusFn: AnyRequestActionType<SafeAnyType>['apiFunction']; // must be memoized
  exportFn: AnyRequestActionType<SafeAnyType>['apiFunction']; // must be memoized

  filterFn?: () => UiTableInnerFiltersType;
  customText?: string;
  customExportingText?: string;
  className?: string;
  withIcon?: boolean;
  enabled?: boolean; // former `show`
  data?: SafeAnyType;
  checkStatusData?: SafeAnyType;
  upperCaseExporting?: boolean;
};

/**
 * @deprecated
 * use {@link ExportButtonClean} instead
 */
export const ExportButton: React.FC<Props> = ({
  className,
  enabled = true,
  exportFn,
  checkStatusFn,
  filterFn,
  withIcon = false,
  customText,
  customExportingText,
  data = null,
  checkStatusData = null,
  upperCaseExporting = true,
}: Props) => {
  const uniqueId = 'anyExport';
  const uniqueIdIsExporting = 'anyExportIsExporting';

  const t = useTHelper('common.exporting');

  const confirm = useContext(ConfirmContext);
  const { response: userInfo } = useSelector(currentUserSelector);

  const [isExporting, setIsExporting] = useState<boolean | null>(null);

  const requestExportStatus = useCallback(
    (channel?: string) => {
      if (channel) {
        PubNub.joinChannel(channel, (message, token) => {
          if (message.message) {
            setIsExporting(false);

            PubNub.leaveChannel(channel, token);
          }
        });
      } else {
        dispatchAnyRequestStart({
          data: checkStatusData || data || undefined,
          apiFunction: checkStatusFn,
          uniqueId: uniqueIdIsExporting,
        });
      }
    },
    [checkStatusData, checkStatusFn, data]
  );

  useEffect(requestExportStatus, [requestExportStatus]);

  /** get exporting result with channel to join to */
  useAnyRequest<APIExportStartReturnType>(uniqueId, {
    onSuccess: ({ ExportFinishedNotificationsGroupName }) => {
      requestExportStatus(ExportFinishedNotificationsGroupName);
    },
  });

  /** handle check is export requests */
  useAnyRequest<AnyExportStatusReturnType>(uniqueIdIsExporting, {
    onSuccess: ({ IsInProgress, ExportFinishedNotificationsGroupName }) => {
      setIsExporting(IsInProgress);

      if (IsInProgress) {
        requestExportStatus(ExportFinishedNotificationsGroupName);
      }
    },
    onError: () => {
      setIsExporting(false);
    },
  });

  const onClick = useCallback(() => {
    if (!isExporting) {
      setIsExporting(true);

      /** start exporting */
      dispatchAnyRequestStart({
        data: filterFn && isFunction(filterFn) ? filterFn() : data,
        apiFunction: exportFn,
        uniqueId,
      });

      confirm({
        header: t('modal.header'),
        body: t('modal.body', {
          email: userInfo?.email || '',
        }),
        successBtnText: t('modal.submitBtnText'),
        singleButton: true,
      });
    }
  }, [isExporting, data, exportFn, filterFn, confirm, t, userInfo?.email]);

  return (
    <UiButton
      tag="a"
      className={classNames(className, {
        'text-uppercase': isExporting && upperCaseExporting,
      })}
      onClick={onClick}
      disabled={isExporting || !enabled || isExporting === null}
    >
      {withIcon && (
        <>
          <i className="glyphicon glyphicon-download icon-white" />
          &nbsp;
        </>
      )}
      {isExporting ? customExportingText || t('exportInProgress') : customText || t('export')}
    </UiButton>
  );
};

export const ExportButtonClean: React.FC<Props> = ({
  className,
  enabled = true,
  exportFn,
  checkStatusFn,
  filterFn,
  withIcon = false,
  customText,
  customExportingText,
  data = null,
  checkStatusData = null,
  upperCaseExporting = true,
}: Props) => {
  const t = useTHelper('common.exporting');

  const confirm = useContext(ConfirmContext);
  const { response: userInfo } = useSelector(currentUserSelector);

  const [isExporting, setIsExporting] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(false);

  const { apiCaller } = useRequest();

  const requestExportStatus = useCallback(
    (channel?: string) => {
      if (channel) {
        PubNub.joinChannel(channel, (message, token) => {
          if (message.message) {
            setIsExporting(false);

            PubNub.leaveChannel(channel, token);
          }
        });
      } else {
        apiCaller(checkStatusFn, checkStatusData || data || undefined)
          .then(({ IsInProgress, ExportFinishedNotificationsGroupName }: AnyExportStatusReturnType) => {
            /** handle check is export requests */
            setIsExporting(IsInProgress);

            if (IsInProgress) {
              requestExportStatus(ExportFinishedNotificationsGroupName);
            }
          })
          .catch(() => {
            setIsExporting(false);
          });
      }
    },
    [apiCaller, checkStatusData, checkStatusFn, data]
  );

  useEffect(requestExportStatus, [requestExportStatus]);

  const onClick = useCallback(() => {
    if (!isExporting) {
      setIsExporting(true);
      setLoading(true);

      /** start exporting */
      apiCaller(exportFn, filterFn && isFunction(filterFn) ? filterFn() : data)
        .then(({ ExportFinishedNotificationsGroupName }: APIExportStartReturnType) => {
          /** get exporting result with channel to join to */
          requestExportStatus(ExportFinishedNotificationsGroupName);

          confirm({
            header: t('modal.header'),
            body: t('modal.body', {
              email: userInfo?.email || '',
            }),
            successBtnText: t('modal.submitBtnText'),
            singleButton: true,
          });
        })
        .catch(() => {
          setIsExporting(false);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [isExporting, apiCaller, exportFn, filterFn, data, confirm, t, userInfo?.email, requestExportStatus]);

  return (
    <UiButton
      tag="a"
      className={classNames(className, {
        'text-uppercase': isExporting && upperCaseExporting,
      })}
      onClick={onClick}
      disabled={isExporting || !enabled || isExporting === null}
      loading={loading}
    >
      {withIcon && (
        <>
          <i className="glyphicon glyphicon-download icon-white" />
          &nbsp;
        </>
      )}
      {isExporting ? customExportingText || t('exportInProgress') : customText || t('export')}
    </UiButton>
  );
};
